import type { LanguageTranslation } from "./translation.types";

const carePathway = "Care pathway";

export const en: LanguageTranslation = {
    name: "English",
    code: "en",

    error: {
        login: "Can't login",
        logout: "Can't logout",
        authRefresh: "Problem with authentication",
        loadMeasurements: "Can't load measurements",
        loadMeasurement: (name) => `Can't load measurement '${name}'`,
        loadChart: "Can't load chart",
        closeMessage: "Close",
        tooFewRespondents:
            "Following selections are excluded because of too few respondents",
    },

    menu: {
        login: "Login",
        logout: "Logout",
        changeLanguage: "Change language",
        changeLanguageTo: (lng) => `Change language: ${lng}`,
    },

    measurements: {
        results: "Results",
        exportResults: "Export results",
        measurementsNationalCurrent: "National current measurements",
        measurementsNationalHistorical: "National historical measurements",
        measurementsRegional: "Regional measurements",
        measurementsPilot: "Pilot measurements",
        measurementsUnknown: "Unknown measurements",
    },

    measurement: {
        measurementYear: "Measurement year",
        publicView: "Public view",
        showResults: "Show results",
        downloadResults: "Download results",
        superGraph: "Super graph",
        freeTextResponses: "Free text answers",
        requireLogin: "Access to these results require login",
        interpretationNoteThat: "Please note that",
        interpretationOptionalSelection:
            "The survey has been conducted with voluntary selections, which means that regions may lack results for certain filters.",
        interpretationVariedComposition:
            "Comparisons between organisations are not always fair as the composition of patients varies, which can affect the results.",
    },

    filters: {
        filters: "Filters",
        organisations: "Organisations",
        organisationsDesc1: "Organisations to compare with",
        organisationsDesc2:
            "Click the checkbox or organisation name to select an organisation",
        organisationsDesc3:
            "Click the right arrow icon to show sub organisations",
        organisationsDesc4:
            "Click the trash can icon to remove a filter selection",
        organisationSearch: "Search organisations",
        organisationSearchResult: "Search result",
        multiSelectionCreate: "Create multi selection",
        multiSelectionSave: "Save multi selection",
        multiSelectionDesc1:
            "This is a multiple selection filter and you can link multiple choices together",
        multiSelectionDesc2:
            "Click the link icon to create a multiple selection",
        multiSelectionDesc3: "Make your choices",
        multiSelectionDesc4:
            "Click the link icon again to save your new multiple selection",
        selectAll: "Select all",
        selectNone: "Select none",
        selectMyRegion: "Select my region",
        latest: "Latest",
        selected: "Selected",
        resetSelected: "Reset selected",
        removeSelection: (name) => `Remove selection: ${name}`,
        selectOption: "Select an option",
        bookmarkDescription:
            "Copy the address or create a bookmark to return to this filter selection later",
        all: "All",
        basedOnMetadataFilters: "Filters based on sample data",
        basedOnQuestionFilters: "Filters based on questionnaire answers",
        basedOnQuestionDesc:
            "Data on mailed questionnaires and response rate cannot be calculated for these filters",
    },

    // Filter names are already in Swedish from the server
    filter: {
        Alla: "All",
        Vårdförlopp: carePathway,

        "Publik organisationsstruktur": "Public organisational structure",
        "Intern organisationsstruktur": "Internal organisational structure",
        "HSA organisationsstruktur": "HSA organisational structure",

        Land: "Country",
        "Regionalt cancercentrum": "Regional cancer center",
        Landsting: "County council",
        "Landsting/region": "County council/region",
        Region: "Region",
        Sjukhus: "Hospital",
        Kommun: "Municipality",
        "Hälso-/vårdcentral": "Health center",
        Klinik: "Clinic",
        Mottagning: "Unit",
        Plats: "Location",

        Lägstanivå: "Lowest level",
        Undernivå: "Sub level",

        Organisationer: "Organisations",
        "Organisationer att jämföra med": "Organisations to compare with",

        Jämförelseperiod: "Comparison period",
        "Tidsperioder att jämföra med": "Time periods to compare with",

        Urvalskriterie: "Selection criteria",
        "Urvalskriterie för enkäten":
            "Selection criteria for the questionnaire",
        Läkarbesök: "Doctor visits",
        "Läkarbesök distans": "Doctor visits distance",
        Sjuksköterskebesök: "Nurse visits",
        "Sjuksköterskebesök distans": "Nurse visits distance",
        "Sjuksköterska distans": "Nurse distance",
        "Besök hos övrig vårdpersonal": "Visits to other healthcare staff",
        "Distanskontakt övrig personal": "Distance contact other staff",

        Enkätversion: "Questionnaire version",
        "Version av enkäten": "The version of the questionnaire",
        Vuxen: "Adult",
        Vårdnadshavare: "Legal guardian",
        "Barn och vårdnadshavare": "Children and legal guardian",

        Kön: "Gender",
        "Det juridiska kön som respondenten har":
            "The legal gender of the respondent",
        Kvinna: "Woman",
        Man: "Man",
        "Kön-uppgift saknas": "Gender info missing",

        Könsidentitet: "Gender identity",
        "Den könsidentitet som respondenten har":
            "The gender identity of the respondent",
        "Annat kön": "Other gender",
        "Könsidentitet-uppgift saknas": "Gender identity info missing",

        Åldersgrupp: "Age group",
        "Respondentens åldersgrupp": "The respondents age group",
        "Åldersgrupp-uppgift saknas": "Age group info missing",

        Respondent: "Respondent",
        "Den som svarade på enkäten":
            "The person who answered the questionnaire",
        Patient: "Patient",
        "Patient med närstående": "Patient with a relative",
        Närstående: "Relative",
        "Respondent-uppgift saknas": "Respondent info missing",

        Utbildningsnivå: "Level of education",
        "Respondentens utbildningsnivå": "The respondents level of education",
        Grundskola: "Elementary school",
        Gymnasium: "Secondary school",
        Yrkesutbildning: "Vocational education",
        "Eftergymnasial utbildning": "University",
        "Universitet 1-2 år": "University 1-2 years",
        "Universitet 3+ år": "University 3+ years",
        "Ingen utbildning": "No education",
        "Okänd utbildning": "Unknown education",
        "Utbildningsnivå-uppgift saknas": "Education info missing",

        Sysselsättning: "Employment",
        "Respondentens sysselsättning": "The respondents employment",
        Arbetar: "Employed",
        Arbetslös: "Unemployed",
        Studerar: "Studying",
        Pensionär: "Pensioner",
        "Övrig sysselsättning": "Other employment",
        "Sysselsättning-uppgift saknas": "Employment info missing",

        Cancerdiagnos: "Cancer diagnosis",
        "Utredningsresultat efter genomfört vårdförlopp":
            "Resulting cancer diagnosis after performed investigation",
        "Med cancerdiagnos": "With cancer diagnosis",
        "Utan cancerdiagnos": "Without cancer diagnosis",

        Överflyttade: "Moved patients",
        "Invånare i eget län": "Citizens in own county",
        "Patienter i eget län": "Patients in own county",
        "Endast invånare vårdade i annat län":
            "Only citizens given care in another county",
        "Endast patienter folkbokförda i annat län":
            "Only patients registered in another county",

        Ambulanstyp: "Ambulance type",
        Ambulans: "Ambulance",
        Helikopterambulans: "Helicopter ambulance",
        "Utan ambulansfärd": "Without ambulance transport",
        "Ambulanstyp-uppgift saknas": "Ambulance type info missing",

        Ursprung: "Origin",
        "Respondentens ursprung": "The respondents origin",
        "Född i Sverige": "Born in Sweden",
        "Född i övriga Norden": "Born in other Nordic country",
        "Född i övriga Europa": "Born in other European country",
        "Född i övriga världen": "Born in other country",
        "Ursprung-uppgift saknas": "Origin info missing",

        Hälsotillstånd: "Health condition",
        "Respondentens självskattade hälsotillstånd":
            "The respondents self-rated health condition",
        "Mycket bra hälsotillstånd": "Very good health condition",
        "Bra hälsotillstånd": "Good health condition",
        "Någorlunda hälsotillstånd": "Fair health condition",
        "Dåligt hälsotillstånd": "Poor health condition",
        "Mycket dåligt hälsotillstånd": "Very poor health condition",
        "Hälsotillstånd-uppgift saknas": "Health condition info missing",

        "Ny-/återbesök": "New/return visits",
        Nybesök: "New visits",
        Återbesök: "Return visits",

        "Typ av besök": "Type of visit",
        "Besök på plats": "On-site visit",
        Distanskontakt: "Remote contact",

        Kontakttyp: "Contact type",
        Gruppbesök: "Group visit",
        "Individuella besök": "Individual visit",
        Teambesök: "Team visit",

        "Vem svarade": "Who answered",
        "Jag som patient": "I as a patient",
        "Jag som patient tillsammans med en närstående":
            "I as a patient together with a relative",
        "Jag som närstående": "I as a relative",
        "Vem svarade-uppgift saknas": "Who answered info missing",

        Verksamhetskod: "Activity code",
        "Habilitering, barn och ungdom": "Rehabilitation, children and youth",
        "Habilitering, vuxna": "Rehabilitation, adults",
        Hjälpmedelsverksamhet: "Assistive device service",
        Syncentral: "Vision center",

        Remittenttyp: "Remittance type",
        Primärvård: "Primary care",
        Specialiserad: "Specialized care",

        Yrkeskategori: "Professional category",
        Läkare: "Doctor",
        Sjuksköterska: "Nurse",
        Distriktssjuksköterska: "District nurse",
        Psykolog: "Psychologist",
        Kurator: "Counselor",
        Psykoterapeut: "Psychotherapist",
        Samtalsterapeut: "Talk therapist",
        "Fysioterapeut/Sjukgymnast": "Physiotherapist",
        Arbetsterapeut: "Occupational therapist",
        Logoped: "Speech therapist",
        Dietist: "Dietitian",
        Audionom: "Audiologist",
        Specialpedagog: "Special education teacher",
        Optiker: "Optician",
        Kiropraktor: "Chiropractor",
        Naprapat: "Naprapath",
        Lymfterapeut: "Lymphatic therapist",
        Stomiterapeut: "Ostomy therapist",
        Synpedagog: "Vision educator",
        Övriga: "Other",
    },

    stats: {
        dimensionScores: "Dimension values",
        dimensionScore: "Dimension value",
        dimensionScoreYAxis: "Dimension value (Desired value high)",
        dimensionScoreDesc: "Mean value of all questions in the dimension",
        dimensionScoreTotal: "Overall experience",
        dimensionScoreTotalYAxis: "Overall experience (Desired value high)",
        dimensionScoreTotalParentheses: "Mean value dimension questions",
        dimensionScoreTotalDesc:
            "Average of all questions included in all dimensions",
        spiderChart: "Spider chart",
        barChart: "Bar chart",
        tableChart: "Table",
        questions: "Questions",
        questionOverview: "Question overview",
        questionOverviewAllSeries: "Mean value dimension questions: All",
        questionOverviewTotal: "Question overview dimension questions",
        dimensionsLink: "Read more about the dimensions here",
        choicesPercentage: "Percentage answer options",
        choicesPercentageYAxis: "Percentage answer options (%)",
        meanValue: "Mean value",
        meanValueYAxis: "Mean value (Desired value high)",
        numRespondents: "Number of respondents",
        numRespondentsOfTotal: (num, total) => `${num} of ${total}`,
        mailings: "Mailings",
        mailingsNum: "Mailed questionnaires",
        mailingsYAxis: "Number (k = 1000)",
        answeredNum: "Answered questionnaires",
        responseRate: "Response rate",
        responseRateYAxis: "Response rate (%)",
        basedOnQuestionWarn:
            "cannot be calculated for filters based on questionnaire answers",
        showQuestions: "Show questions",
    },

    dimensions: {
        Helhetsintryck: "Overall impression",
        "Emotionellt stöd": "Emotional support",
        "Delaktighet och involvering": "Participation and involvement",
        "Respekt och bemötande": "Respect and treatment",
        "Kontinuitet och koordinering": "Continuity and coordination",
        "Information och kunskap": "Information and knowledge",
        "Vård och behandling": "Care and treatment",
        Delaktighet: "Participation",
        Bemötande: "Treatment",
        Kontinuitet: "Continuity",
        Information: "Information",
        Tillgänglighet: "Availability",
        "Trygghet och säkerhet": "Safety and security",
        Levnadsvanor: "Questions about living habits",
        "Nationella läkemedelslistan": "National medication list",
        "Om patienten": "Questions about the patient",
        Funktionshinder: "Disability",
        "Coronarelaterade frågor": "Corona-related questions",
        "Barn och ungdom": "Children and youth",
        "Barn 8-14 år": "Children 8-14 years",
        Övrigt: "Other questions",
        "Övriga frågor": "Other questions",
        "Regionala extrafrågor": "Regional extra questions",
        Dold: "Hidden",
        Öppet: "Free text",
        "Öppet barn": "Free texts about children",
    } satisfies Required<LanguageTranslation["dimensions"]>,

    download: {
        filtersDesc:
            "Filters for the download of statistics and standard report.",
        downloadAggregated: "Download statistics (excel)",
        lowestLevel: "Lowest level",
        showUnits: "Show units",
        hideUnits: "Hide units",
        downloadStandardReport: "Download standard report (powerpoint)",
        standardReportDesc1:
            "It can take several minutes to download the report since much date that needs to be calculated.",
        standardReportDesc2:
            "You can select the country, several regions or several units. You can Not combine between the different levels.",
        downloadReport: "Download report",
        standardReportIncludeHistory: "Include history",
        standardReportIncludeHistoryTooltip: "History includes previous years",
        standardReportIncludeQuestions: "Include questions",
        standardReportSelectedOrg: "Selected organisations",
        downloadQuestionnaire: "Download questionnaire (excel)",
        selectedQuestionnaire: "Selected questionnaire",
        downloadRegionalRawData: "Download regional raw data (csv)",
        includeFreeText: "Include free text questions",
    },

    freeText: {
        noOrganisationSelected: "All",
        filters: "Filters",
        question: "Question",
        cantFindAnswers: "Can't find any free text answers",
        questionAnswers: "Free text answers",
        numPerPage: "Answers per page",
        paginatedFreeTextAnswers: "Paginated free text answers",
        showingAnswers: "Showing answers",
        filterFreeTextAnswers: "Filter free text answers",
        resultsFreeTextAnswers: "Results free text answers",
        downloadFreeTextExport: "Download free text",
        organisation: "Organization",
        child: "Child",
        svf: carePathway,
        periodMonth: "Month",
        periodQuarter: "Quarter",
        overallScore: "Overall impression",
        categories: "Categories",
    },

    pagination: {
        paginationTop: "Pagination top",
        paginationBottom: "Pagination bottom",
        first: "First",
        last: "Last",
        previous: "Previous",
        next: "Next",
    },

    months: {
        1: "January",
        2: "February",
        3: "March",
        4: "April",
        5: "May",
        6: "June",
        7: "July",
        8: "August",
        9: "September",
        10: "October",
        11: "November",
        12: "December",
    },
    // English is default language and built in
    highcharts: {
        contextButtonTitle: "Export charts",
        downloadPNG: "Download chart as PNG",
        downloadJPEG: "Download chart as JPEG",
        downloadSVG: "Download chart as SVG",
        downloadPDF: "Download chart as PDF",
        downloadCSV: "Download chart as CSV",
        downloadXLS: "Download chart as XLS",
        printChart: "Print chart",
        viewFullscreen: "View chart in fullscreen",
    },

    misc: {
        help: "Help",
        back: "Back",
    },
};
